import React, { useEffect, useState, useRef, useCallback, memo } from 'react';
import "@arcgis/core/assets/esri/themes/light/main.css";
import Map from "@arcgis/core/Map.js";
import Extent from "@arcgis/core/geometry/Extent.js";
import MapView from "@arcgis/core/views/MapView.js";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer.js";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer.js";
import Graphic from "@arcgis/core/Graphic.js";
import Point from "@arcgis/core/geometry/Point.js";
import TextSymbol from "@arcgis/core/symbols/TextSymbol.js";
import * as locator from "@arcgis/core/rest/locator.js";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine.js";
import * as projection from "@arcgis/core/geometry/projection.js";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import Polygon from "@arcgis/core/geometry/Polygon.js";
import './esrimap.css';
import config from '../../../config/config';
import { map_message, page_type } from '@constants/esriMapConstant';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { setGeometryMapPoint, setMapManualAddress, setMapPincodeFirstTimeEdit, setCreateMapPincodeWithHfr, setMapServicesDown } from '@store/actions/facilityGeoMapAction';
import facilityMarker2 from '@assets/facility_images/facilityMarker2.png';
import CloseModalMap from './CloseModal';

const locatorUrl = config.locateUrl;

const FacilityESRIMap = (location) => {
    const [showModal, setShowModal] = useState({ modalOpen: false, type: '' });
    const dispatch = useDispatch();
    const facilityGeoMap = useSelector((state) => state.facilityGeoMap);

    let facilityGeoMapref = useRef(facilityGeoMap);
    let distFenceGeometry = useRef(null);
    const polygonGeometry = useRef(null);
    let locateEventref = useRef(null);

    useEffect(() => {
        facilityGeoMapref.current = facilityGeoMap;
    }, [facilityGeoMap]);

    const layer1 = new FeatureLayer({
        url: config.boundaryUrl?.state,
        customParameters: {
            "Token": config.mapTokens[config.asstURL].state
        },
        visible: false,
        labelsVisible: false
    });

    const layer2 = new FeatureLayer({
        url: config.boundaryUrl?.district,
        customParameters: {
            "Token": config.mapTokens[config.asstURL].district
        },
        labelsVisible: false,
    });

    const layer3 = new FeatureLayer({
        url: config.boundaryUrl?.block,
        customParameters: {
            "Token": config.mapTokens[config.asstURL].block
        },
        outFields: ["*"],
        labelsVisible: false,
    });

    let mapRef = useRef(null);
    const pointerGraphicLayers = new GraphicsLayer();
    const distFenceGraphicLayers = new GraphicsLayer();
    const blockLableLayers = new GraphicsLayer();
    const polygonGraphicLayers = new GraphicsLayer();
    let isDragging = useRef(false);

    let map = new Map({
        layers: [layer1]
    });

    const updatePolygone = useCallback((coordinates) => {
        const polygon = new Polygon({
            rings: [coordinates],
            spatialReference: { wkid: 4326 }
        });
        polygonGeometry.current = polygon;
        const polygonGraphic = new Graphic({
            geometry: polygon,
            symbol: {
                type: "simple-fill",
                color: [175, 45, 148, 0.6],
                outline: {
                    color: [175, 45, 148, 0.6],
                    width: 0.5
                }
            }
        });
        polygonGraphicLayers.removeAll();
        polygonGraphicLayers.add(polygonGraphic);
    }, []);

    let locateMarker = null;
    const updateMarker = useCallback((point) => {
        if (locateMarker) {
            pointerGraphicLayers.remove(locateMarker);
        }

        locateMarker = new Graphic({
            geometry: point,
            symbol: {
                type: "picture-marker",
                url: facilityMarker2,
                width: "30px",
                height: "30px"
            }

        });

        pointerGraphicLayers.add(locateMarker);
    }, []);

    const createPoint = useCallback((longitude, latitude) => {
        let point = new Point({
            longitude: longitude,
            latitude: latitude
        });

        return point;
    }, []);

    const updateLatLong = useCallback((longitude, latitude, mapView) => {
        mapView.graphics.removeAll();
        let point = createPoint(longitude, latitude);

        updateMarker(point);
    }, [createPoint, updateMarker]);

    let mapView = useRef(null);

    const clickedEvent = (locateEvent) => {
        const mapPointer = mapView.current.toMap({ x: locateEvent.x, y: locateEvent.y });
        let point = createPoint(locateEvent.mapPoint.longitude, locateEvent.mapPoint.latitude);
        if (mapPointer && geometryEngine.contains(distFenceGeometry.current, mapPointer)) {
            updateLatLong(locateEvent.mapPoint.longitude, locateEvent.mapPoint.latitude, mapView.current);
            location.onLocationChange ? location.onLocationChange({ latitude: locateEvent.mapPoint.latitude, longitude: locateEvent.mapPoint.longitude }) : '';
            locator.locationToAddress(locatorUrl, { location: point, }).then((addressLocation) => {
                (location.selectedAddressValue && location.selectedAddressValue.current) ? location.selectedAddressValue.current.value = addressLocation.attributes.Match_addr : '';
                location.onPostalCodeChange ? location.onPostalCodeChange(addressLocation.attributes.Postal) : '';
            });
            dispatch(setGeometryMapPoint({ mapPointer: mapPointer, distFenceGeometry: distFenceGeometry.current }));
        }

        if (facilityGeoMapref.current.editFirstTimePincode && facilityGeoMapref.current.editFirstTimePincode.flow === 'edit') {
            dispatch(setMapPincodeFirstTimeEdit(
                {
                    flow: 'edit',
                    firstTime: false
                }
            ));
        } else {
            dispatch(setMapPincodeFirstTimeEdit(null));

            if (facilityGeoMapref.current.addPincodeWithHfr && facilityGeoMapref.current.addPincodeWithHfr.hfrVerified === true) {
                dispatch(setCreateMapPincodeWithHfr(
                    {
                        hfrVerified: true,
                        pincodeWithHfr: false
                    }
                ));
            } else {
                dispatch(setCreateMapPincodeWithHfr(null));
            }
        }
    };

    const pointerUpEvent = (event) => {
        const point = mapView.current.toMap({ x: event.x, y: event.y });
        if (isDragging.current && geometryEngine.contains(distFenceGeometry.current, point)) {
            dispatch(setGeometryMapPoint({ mapPointer: point, distFenceGeometry: distFenceGeometry.current }));
            location.onLocationChange ? location.onLocationChange({ latitude: point.latitude, longitude: point.longitude }) : '';
            locator.locationToAddress(locatorUrl, { location: point }).then((addressLocation) => {
                (location.selectedAddressValue && location.selectedAddressValue.current) ? location.selectedAddressValue.current.value = addressLocation.attributes.Match_addr : '';
                location.onPostalCodeChange ? location.onPostalCodeChange(addressLocation.attributes.Postal) : '';
            });
            isDragging.current = false;
        } else {
            isDragging.current = false;
        }

        if (facilityGeoMapref.current.editFirstTimePincode && facilityGeoMapref.current.editFirstTimePincode.flow === 'edit') {
            dispatch(setMapPincodeFirstTimeEdit(
                {
                    flow: 'edit',
                    firstTime: false
                }
            ));
        } else {
            dispatch(setMapPincodeFirstTimeEdit(null));

            if (facilityGeoMapref.current.addPincodeWithHfr && facilityGeoMapref.current.addPincodeWithHfr.hfrVerified === true) {
                dispatch(setCreateMapPincodeWithHfr(
                    {
                        hfrVerified: true,
                        pincodeWithHfr: false
                    }
                ));
            } else {
                dispatch(setCreateMapPincodeWithHfr(null));
            }
        }
    }

    const closeMapModal = (data) => {
        if (data === true) {
            if (showModal.type === 'click') {
                clickedEvent(locateEventref.current);
            } else {
                pointerUpEvent(locateEventref.current);
            }

            dispatch(setMapManualAddress(false));
        } else {
            if (showModal.type === 'pointer-up') {
                updateLatLong(facilityGeoMapref.current.locationData.longitude, facilityGeoMapref.current.locationData.latitude, mapView.current);
                isDragging.current = false;
            }
        }
        locateEventref.current = null;
        setShowModal({ modalOpen: false, type: '' });
    }

    const handleErrorMessage = () => {
        dispatch(setMapServicesDown(true));
        if (location.pageType == page_type.health_facility) {
            if (facilityGeoMapref.current.editFirstTimePincode && facilityGeoMapref.current.editFirstTimePincode.flow === 'edit') {
                toast.warning(map_message.errorMessageFacilityUpdate, {
                    position: "top-center",
                });
            } else {
                if (facilityGeoMapref.current.addPincodeWithHfr && facilityGeoMapref.current.addPincodeWithHfr.hfrVerified === true) {
                    if (facilityGeoMapref.current.addPincodeWithHfr.checkDataFromAbdm === true) {
                        location.onformFieldChange ? location.onformFieldChange(true) : '';
                        toast.warning(map_message.errorMessageFacilityCreateAbdm, {
                            position: "top-center",
                        });
                    } else {
                        toast.warning(map_message.errorMessageFacilityCreate, {
                            position: "top-center",
                        });
                    }
                } else {
                    toast.warning(map_message.errorMessageFacilityCreate, {
                        position: "top-center",
                    });
                }
            }
        } else if (location.pageType == page_type.session_site_vhnd) {
            location.onformFieldChange ? location.onformFieldChange(true) : '';
            toast.warning(map_message.errorMessageSessionSite, {
                position: "top-center",
            });
        }
    }

    const intialExtent = new Extent({
        xmin: 66.62,
        ymin: 5.23,
        xmax: 98.87,
        ymax: 38.59,
        spatialReference: { wkid: 4326 }
    });

    const lableUpdate = useCallback((layerView) => {
        layerView.queryFeatures({
            returnGeometry: true,
            outFields: ["block_name"],
            where: "1=1"
        }).then((response) => {
            blockLableLayers.removeAll();
            response?.features.forEach((ele) => {
                const textSymbole = new TextSymbol({
                    text: ele.attributes.block_name,
                    color: "black",
                    font: {
                        size: '9px',
                        family: 'Roboto Condensed', style: 'italic', weight: 'normal'
                    },
                });

                const lableGraphic = new Graphic({
                    geometry: ele?.geometry?.centroid || ele?.geometry?.extent?.center,
                    symbol: textSymbole
                });

                blockLableLayers.add(lableGraphic);
            });

        });
    }, []);

    const initMapView = useCallback((location) => {
        mapView.current = new MapView({
            container: mapRef.current,
            map: map,
            constraints: {
                minZoom: 4
            },
            center: [78.9629, 20.5937],
            scale: 24000,
        });
        mapView.current.when(async () => {
            mapView.current.zoom = 4;
            mapView.current.extent = intialExtent;
            mapView.current.ui.remove("attribution");

            if (location.dtCode && location.dtCode?.dtCode) {
                const Layer2query = layer2.createQuery();
                Layer2query.where = `dist_lgd = ${location.dtCode.dtCode}`;
                Layer2query.returnGeometry = true;

                layer2.queryFeatures(Layer2query)
                    .then(function (response) {
                        dispatch(setMapServicesDown(false));
                        location.onformFieldChange ? location.onformFieldChange(false) : '';
                        distFenceGraphicLayers.removeAll();
                        distFenceGeometry.current = response?.features?.[0].geometry;
                        dispatch(setGeometryMapPoint({ mapPointer: null, distFenceGeometry: distFenceGeometry.current }));
                        const outlineGraphic = new Graphic({
                            geometry: response?.features?.[0].geometry,
                            symbol: {
                                type: "simple-line",
                                color: [175, 45, 148],
                                width: 2
                            }
                        })
                        distFenceGraphicLayers.add(outlineGraphic);

                        mapView.current.goTo({
                            target: response?.features?.[0].geometry.extent,
                        });

                        layer3.definitionExpression = `dist_lgd = ${location.dtCode.dtCode}`;
                        blockLableLayers.removeAll();
                        map.add(blockLableLayers, 1);
                        map.add(layer3, 2);
                        layer3.load().then(() => {
                            mapView.current.whenLayerView(layer3).then((layerView) => {
                                reactiveUtils.watch(
                                    () => layerView.updating, (updating) => {
                                        if (!updating) {
                                            lableUpdate(layerView)
                                        }
                                    }
                                );
                            })
                        }).catch((error) => { });
                        map.add(distFenceGraphicLayers, 3);
                        if (location.pageType == page_type.session_site_vhnd) {
                            location.coverageAreaCoordinates ? updatePolygone(location.coverageAreaCoordinates) : '';
                            map.add(polygonGraphicLayers, 4)
                            map.add(pointerGraphicLayers, 5);
                        } else {
                            map.add(pointerGraphicLayers, 4);
                        }

                        if ((location.latitude !== 0 && location.latitude !== '') && (location.longitude !== 0 && location.longitude !== '')) {
                            let point = createPoint(location.longitude, location.latitude);

                            const projectedPiont = projection.project(point, distFenceGeometry.current.spatialReference);
                            updateLatLong(location.longitude, location.latitude, mapView.current);
                            locator.locationToAddress(locatorUrl, { location: point, }).then((addressLocation) => {
                                if (facilityGeoMapref.current.isManualLatLongClicked === true) {
                                    (location.selectedAddressValue && location.selectedAddressValue.current) ? location.selectedAddressValue.current.value = addressLocation.attributes.Match_addr : '';
                                } else {
                                    (location.selectedAddressValue && location.selectedAddressValue.current) ? location.selectedAddressValue.current.value = location.selectedAddressValue.current?.value !== '' ? location.selectedAddressValue.current?.value : addressLocation.attributes.Match_addr : '';
                                }
                                location.onLocationChange ? location.onLocationChange({ latitude: location.latitude, longitude: location.longitude }) : '';

                                if (location.pageType == page_type.health_facility) {
                                    if (facilityGeoMapref.current.editFirstTimePincode && facilityGeoMapref.current.editFirstTimePincode.flow === 'edit') {
                                        if (facilityGeoMapref.current.editFirstTimePincode.firstTime === false) {
                                            location.onPostalCodeChange ? location.onPostalCodeChange(addressLocation.attributes.Postal) : '';
                                        }
                                    } else {

                                        if (facilityGeoMapref.current.addPincodeWithHfr && facilityGeoMapref.current.addPincodeWithHfr.hfrVerified === true) {
                                            if (facilityGeoMapref.current.addPincodeWithHfr.pincodeWithHfr === false) {
                                                location.onPostalCodeChange ? location.onPostalCodeChange(addressLocation.attributes.Postal) : '';
                                            }
                                        } else {
                                            location.onPostalCodeChange ? location.onPostalCodeChange(addressLocation.attributes.Postal) : '';
                                        }

                                    }
                                } else if (location.pageType == page_type.session_site_vhnd) {
                                    location.onPostalCodeChange ? location.onPostalCodeChange(addressLocation.attributes.Postal) : '';
                                }

                            });
                            dispatch(setGeometryMapPoint({ mapPointer: projectedPiont, distFenceGeometry: distFenceGeometry.current }));
                            if (!geometryEngine.contains(distFenceGeometry.current, projectedPiont)) {
                                let errorMessage = '';
                                if (location.pageType == page_type.health_facility) {
                                    errorMessage = map_message.facilityGeometryMessage;
                                } else if (location.pageType == page_type.session_site_vhnd) {
                                    errorMessage = map_message.sessionGeometryMessage;
                                }
                                toast.error(errorMessage, {
                                    position: "top-center",
                                });
                            }

                        } else {
                            pointerGraphicLayers.removeAll()
                        }
                    }).catch((error) => {
                        handleErrorMessage();
                    });
            } else {
                handleErrorMessage();
            }

            {location.pageType !== page_type.approval_view && mapView.current.on('click', (locateEvent) => {

                const mapPointer = mapView.current.toMap({ x: locateEvent.x, y: locateEvent.y });
                if (mapPointer && geometryEngine.contains(distFenceGeometry.current, mapPointer)) {
                    if (facilityGeoMapref.current.isManualAddress === true) {
                        setShowModal({ modalOpen: true, type: 'click' });
                        locateEventref.current = locateEvent;
                    } else {
                        clickedEvent(locateEvent);
                    }
                }
            });}

            mapView.current.on('drag', (event) => {
                if (isDragging.current) {
                    event.stopPropagation();
                }
            });

            mapView.current.on('pointer-down', (event) => {
                const mapPointer = mapView.current.toMap({ x: event.x, y: event.y });

                mapView.current.hitTest(event).then((response) => {
                    if (response?.results?.length > 0 && response?.results?.[0].graphic === locateMarker) {
                        if (mapPointer && geometryEngine.contains(distFenceGeometry.current, mapPointer)) {
                            isDragging.current = true;
                        }
                    }
                })

            });

            mapView.current.on('pointer-move', (event) => {
                if (isDragging.current) {
                    const mapPointer = mapView.current.toMap({ x: event.x, y: event.y });
                    if (mapPointer && geometryEngine.contains(distFenceGeometry.current, mapPointer)) {
                        updateMarker(mapPointer);
                    }
                }
            });



            mapView.current.on('pointer-up', (event) => {
                if (isDragging.current) {
                    if (facilityGeoMapref.current.isManualAddress === true) {
                        setShowModal({ modalOpen: true, type: 'pointer-up' });
                        locateEventref.current = event;
                    } else {
                        pointerUpEvent(event);
                    }
                }
            });


        })
    }, [updateLatLong, createPoint]);

    useEffect(() => {
        initMapView(location);
    }, [location]);

    return (
        <>
            <div style={{ width: '100%', height: '100%' }} ref={mapRef}></div>
            <CloseModalMap showModal={showModal.modalOpen} closeModal={closeMapModal} />
        </>
    );
};

const MemoizedFacilityESRIMap = memo(FacilityESRIMap);
MemoizedFacilityESRIMap.displayName = "FacilityESRIMap"

export default MemoizedFacilityESRIMap;